import {
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { LockIcon } from "@icons";
import { useColor } from "@styles/useColor";
import { type ImplicitAccount } from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { decrypt } from "@umami/crypto";
import {
  type AccountsState,
  type Backup,
  encryptSeedphrasesAndSecretkeys,
  useAsyncActionHandler,
  useLoginToWallet,
  useRestoreBackup,
  validateBackup,
} from "@umami/state";
import { useCallback, useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { type FormFields } from "./types";
import { ModalBackButton } from "../BackButton";
import { SetupPassword } from "../Onboarding/SetupPassword";
import { PasswordInput } from "../PasswordInput";
import { getEncryptedBackupForPasskey } from "./utils";

import { usePasskey } from "@/hooks/usePasskey";
import { trackButtonClick } from "@/utils/analytics";
import { setupPersistence } from "@/utils/store";

type SetupPasswordProps =
  | {
      isSetNewPassword: false;
      rawBackup: string;
    }
  | {
      isSetNewPassword: true;
      onSubmit: (backupPassword: string, onClose: () => void) => void;
    };

export const DEFAULT_CURVE = "secp256k1";

export const SetupBackupPassword = (props: SetupPasswordProps) => {
  const { isSetNewPassword } = props;
  const color = useColor();
  const { handleAsyncAction } = useAsyncActionHandler();

  const form = useMultiForm<FormFields>({
    mode: "onBlur",
  });

  const {
    formState: { isValid },
    getValues,
  } = form;

  const [decryptedBackup, setDecryptedBackup] = useState<Backup | undefined>(undefined);

  const { handleLogin } = useLoginToWallet(
    decryptedBackup?.accounts.defaultAccount as ImplicitAccount,
    setupPersistence
  );

  const restoreBackup = useRestoreBackup();
  const { openWith, onClose } = useDynamicModalContext();
  const { isPasskeyFlow, register, isLoading, isPasskeyUser } = usePasskey();

  const onNewPassword = useCallback(
    async ({
      newPassword,
      backup,
      encryptedAccountsPasskey = {},
    }: {
      newPassword: string;
      backup: Backup;
      encryptedAccountsPasskey?: AccountsState | Record<string, never>;
    }) => {
      // Re-encrypt the seed phrases and secret keys using either the master password or passkey
      const encryptedAccountsState = {
        ...backup.accounts,
        ...encryptedAccountsPasskey,
        ...(await encryptSeedphrasesAndSecretkeys({ backup, password: newPassword })),
      } as AccountsState;

      restoreBackup({
        ...backup,
        accounts: encryptedAccountsState,
      });
      await handleLogin()(encryptedAccountsState, { password: newPassword });

      onClose();
    },
    [handleLogin, onClose, restoreBackup]
  );

  const setNewEncryption = async ({ decryptedBackup }: { decryptedBackup: Backup }) => {
    if (isPasskeyFlow && isPasskeyUser) {
      const { password, accountState } = await getEncryptedBackupForPasskey(
        decryptedBackup,
        register
      );

      if (password) {
        await onNewPassword({
          backup: decryptedBackup,
          newPassword: password,
          encryptedAccountsPasskey: accountState,
        });
      }
    } else {
      await openWith(
        <SetupPassword
          mode="import_backup"
          onSubmit={async (password: string) => {
            await onNewPassword({ backup: decryptedBackup, newPassword: password });

            trackButtonClick("create_password", {
              button_label: "Import wallet",
              section: "Welcome",
              action: "button_click",
              flow: "Onboarding",
            });
          }}
        />
      );
    }
  };

  useEffect(() => {
    if (decryptedBackup) {
      void setNewEncryption({ decryptedBackup });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedBackup]);

  const title = isSetNewPassword ? "Setup backup password" : "Enter backup password";
  const subtitle = isSetNewPassword
    ? "Set a password to secure your backup file."
    : "Enter the password to decrypt your backup file.";
  const buttonLabel = isSetNewPassword ? "Download" : "Confirm";
  const icon = LockIcon;

  const decryptBackup = async (backupPassword: string, rawBackup: string): Promise<Backup> => {
    const encryptedBackup = JSON.parse(rawBackup);
    const decryptedBackupRaw = await decrypt(encryptedBackup, backupPassword);

    return JSON.parse(decryptedBackupRaw);
  };

  const handleSubmit = async (values: FormFields) =>
    handleAsyncAction(async () => {
      if (isSetNewPassword) {
        props.onSubmit(values.password, onClose);
      } else {
        const backupPassword = values.password;
        const decryptedBackup = await decryptBackup(backupPassword, props.rawBackup);
        validateBackup(decryptedBackup);
        setDecryptedBackup(decryptedBackup);
      }
      trackButtonClick("continue_with_backup_password", {
        button_label: "Confirm",
        section: "Welcome",
        action: "button_click",
        flow: "Onboarding",
      });
    });

  return (
    <ModalContent>
      <ModalHeader>
        <ModalBackButton />
        <ModalCloseButton />
        <Center flexDirection="column" gap="12px" whiteSpace="break-spaces">
          <Icon as={icon} width="24px" height="24px" color={color("400")} />
          <Heading marginTop="4px" size="xl">
            {title}
          </Heading>
          <Text width="full" color={color("700")} fontWeight="400" textAlign="center" size="md">
            {subtitle}
          </Text>
        </Center>
      </ModalHeader>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <ModalBody>
            <Flex flexDirection="column" gap="24px">
              <PasswordInput
                inputName="password"
                isStrengthCheckEnabled={isSetNewPassword}
                label={isSetNewPassword ? "Set password" : "Enter password"}
                required="Password is required"
              />
              {isSetNewPassword && (
                <PasswordInput
                  inputName="passwordConfirmation"
                  label="Confirm password"
                  required="Password confirmation is required"
                  validate={value => value === getValues("password") || "Passwords do not match"}
                />
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              width="full"
              isDisabled={!isValid}
              isLoading={isLoading}
              type="submit"
              variant="primary"
            >
              {buttonLabel}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalContent>
  );
};
